import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Climatizacao, PapelDaAdega } from '@adega/shared';
import { EscolhaClimatizacao } from '../components/EscolhaClimatizacao.js';
import { EscolhaPapel } from '../components/EscolhaPapel.js';
import { ErrorBox } from '../components/Feedback.js';
import { PageHeader } from '../components/PageHeader.js';
import { useCellars, useCreateCellar } from '../api/queries.js';

interface PrateleiraForm {
  label: string;
  capacity: number;
}

/**
 * Assistente de criação de adega — serve tanto para a primeira quanto para as
 * seguintes, já que a casa pode ter mais de uma adega física.
 */
export function CellarSetup() {
  const navigate = useNavigate();
  const { data: cellars } = useCellars();
  const criar = useCreateCellar();

  const primeira = !cellars || cellars.length === 0;

  const [nome, setNome] = useState(primeira ? 'Minha Adega' : '');
  const [climatizacao, setClimatizacao] = useState<Climatizacao | null>(null);
  const [papel, setPapel] = useState<PapelDaAdega | null>(null);
  const [quantidade, setQuantidade] = useState(4);
  const [porPrateleira, setPorPrateleira] = useState(8);
  const [prateleiras, setPrateleiras] = useState<PrateleiraForm[] | null>(null);

  const lista =
    prateleiras ??
    Array.from({ length: Math.max(1, quantidade) }, (_, i) => ({
      label: `Prateleira ${i + 1}`,
      capacity: porPrateleira,
    }));

  const total = lista.reduce((soma, p) => soma + p.capacity, 0);

  const salvar = async () => {
    const criada = await criar.mutateAsync({
      name: nome.trim() || 'Minha Adega',
      // Não escolher é legítimo: fica "não informado", e a IA não afirma nada
      // sobre a condição de guarda.
      ...(climatizacao ? { climatizacao } : {}),
      ...(papel ? { papel } : {}),
      shelves: lista.map((p) => ({ label: p.label.trim() || undefined, capacity: p.capacity })),
    });
    navigate(`/adega/${criada.id}`);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        titulo={primeira ? 'Bem-vindo ao Minha Adega' : 'Nova adega'}
        voltar={primeira ? undefined : true}
      />
      {/*
        Esta e a PRIMEIRA tela de quem entra por convite, e ate aqui ela abria
        fria: o titulo dizia "Configurar sua adega" e o texto ja emendava em
        "informe quantas prateleiras". Quem clicou num link de e-mail nao sabe o
        que e este app, nem que "adega" aqui e o MOVEL, e nao a colecao -- eram
        numeros pedidos antes de dizer para que servem.

        Com zero adegas o app nao consegue fazer mais nada (sem garrafa nao ha
        "beber antes" nem "harmonizar"), entao cair direto no assistente
        continua certo: quando so existe uma coisa a fazer, fazer ela e o
        caminho. O que faltava era contexto, e contexto e texto -- nao uma tela
        nova nem um tour, que seria estado para guardar e algo que todo mundo
        que volta tem de fechar.

        Da segunda adega em diante nada disso aparece: quem ja tem uma adega
        conhece o app, e repetir a explicacao seria ruido.
      */}
      {primeira ? (
        <div className="space-y-2 text-sm text-adega-muted">
          <p>
            Aqui você registra os vinhos que tem em casa, e o app diz quais abrir antes de passarem
            do ponto.
          </p>
          <p>
            Para começar, descreva o <strong className="text-adega-text">móvel</strong> onde eles
            ficam: quantas prateleiras tem e quantas garrafas cabem em cada uma. Depois disso você
            cadastra a primeira garrafa fotografando o rótulo.
          </p>
        </div>
      ) : (
        <p className="text-sm text-adega-muted">
          Informe quantas prateleiras ela tem e quantas garrafas cabem em cada uma. Se a última
          prateleira comporta menos garrafas, ajuste individualmente abaixo.
        </p>
      )}

      <div className="card space-y-4">
        <div>
          <label className="label" htmlFor="nome">
            Nome da adega
          </label>
          <input
            id="nome"
            className="field"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex.: Adega da sala, Adega da cozinha"
          />
        </div>

        <div>
          <span className="label">Climatização</span>
          <p className="mb-2 text-xs text-adega-muted">
            Muda a estimativa de quanto tempo cada vinho aguenta guardado. Se não souber agora,
            deixe em branco e informe depois.
          </p>
          <EscolhaClimatizacao valor={climatizacao} onChange={setClimatizacao} />
        </div>

        <div>
          <span className="label">Para que serve esta adega</span>
          {/*
            A saída existe desde sempre no backend (papel `null` = não
            informado), e só a climatização a anunciava. Sem a frase, duas
            perguntas igualmente opcionais liam-se diferente: uma dava
            permissão de pular, a outra parecia obrigatória.

            E esta é a que um convidado novo MENOS tem como responder: ele
            acabou de chegar e ainda não sabe como vai usar o móvel.
          */}
          <p className="mb-2 text-xs text-adega-muted">
            Uma adega de passagem guarda por pouco tempo o que vai ser aberto logo — ali a
            climatização quase não pesa na estimativa. Se ainda não souber, deixe em branco.
          </p>
          <EscolhaPapel valor={papel} onChange={setPapel} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="qtd">
              Prateleiras
            </label>
            <input
              id="qtd"
              type="number"
              min={1}
              max={50}
              className="field"
              value={quantidade}
              onChange={(e) => {
                setQuantidade(Number(e.target.value));
                setPrateleiras(null);
              }}
            />
          </div>
          <div>
            <label className="label" htmlFor="cap">
              Garrafas por prateleira
            </label>
            <input
              id="cap"
              type="number"
              min={1}
              max={200}
              className="field"
              value={porPrateleira}
              onChange={(e) => {
                setPorPrateleira(Number(e.target.value));
                setPrateleiras(null);
              }}
            />
          </div>
        </div>

        <p className="text-sm text-adega-muted">
          Capacidade total: <strong className="text-adega-text">{total} garrafas</strong>
        </p>
      </div>

      <section className="card space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Ajuste por prateleira</h2>
          {prateleiras ? (
            <button type="button" className="btn-ghost text-xs" onClick={() => setPrateleiras(null)}>
              Restaurar padrão
            </button>
          ) : null}
        </div>
        <p className="text-xs text-adega-muted">
          Prateleiras com alturas diferentes comportam quantidades diferentes — é comum a de baixo
          caber menos.
        </p>

        <div className="space-y-2">
          {lista.map((prateleira, indice) => (
            <div key={indice} className="flex gap-2">
              <input
                className="field flex-1"
                value={prateleira.label}
                aria-label={`Nome da prateleira ${indice + 1}`}
                onChange={(e) =>
                  setPrateleiras(
                    lista.map((p, i) => (i === indice ? { ...p, label: e.target.value } : p)),
                  )
                }
              />
              <input
                type="number"
                min={1}
                max={200}
                aria-label={`Capacidade da prateleira ${indice + 1}`}
                className="field w-24"
                value={prateleira.capacity}
                onChange={(e) =>
                  setPrateleiras(
                    lista.map((p, i) =>
                      i === indice ? { ...p, capacity: Number(e.target.value) } : p,
                    ),
                  )
                }
              />
            </div>
          ))}
        </div>
      </section>

      {criar.error ? <ErrorBox erro={criar.error} /> : null}

      <div className="flex gap-2">
        {!primeira ? (
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            Cancelar
          </button>
        ) : null}
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={criar.isPending || total < 1}
          onClick={() => void salvar()}
        >
          {criar.isPending ? 'Criando…' : `Criar adega com ${total} posições`}
        </button>
      </div>
    </div>
  );
}
